import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, Zap, ScanLine, Inbox, Dna, LogOut, Plug } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import CompanySwitcher from "@/components/CompanySwitcher";
import { api } from "@/lib/api";
import { STATUS } from "@/lib/organs";

const NAV = [
  { to: "/", label: "Organismo", icon: Home },
  { to: "/actions", label: "Azioni", icon: Zap },
  { to: "/queue", label: "Coda", icon: Inbox },
  { to: "/dna", label: "DNA", icon: Dna },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { companyId, companies, companiesLoaded } = useCompany();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);

  useEffect(() => {
    if (companyId) api.get("/health").then((r) => setHealth(r.data)).catch(() => setHealth(null));
  }, [companyId]);

  const st = health ? STATUS[health.status] : null;

  return (
    <div className="min-h-screen">
      <header className="fixed top-0 inset-x-0 z-40 glass-strong">
        <div className="max-w-screen-2xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="nav-logo" className="leading-none shrink-0">
            <div className="wordmark text-lg">DAD</div>
            <div className="wordmark-sub text-[8px]">COMPANY BRAIN</div>
          </Link>
          <div className="min-w-0 flex-1 max-w-[260px] [&>div>button]:max-w-full"><CompanySwitcher /></div>

          <nav className="hidden xl:flex items-center gap-1" data-testid="nav-desktop">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm transition-colors ${isActive ? "text-cyan-300 bg-cyan-400/10" : "text-gray-400 hover:text-gray-200"}`}>
                {n.label}
              </NavLink>
            ))}
            <NavLink to="/scan" data-testid="nav-scansiona"
              className="ml-2 btn-primary !py-2 !px-5 text-xs">
              <ScanLine size={16} /> SCANSIONA
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <NavLink to="/integrations/klara" title="Integrazioni" aria-label="Integrazioni" data-testid="nav-integrazioni"
              className={({ isActive }) => `flex items-center justify-center gap-2 min-w-[44px] min-h-[44px] rounded-full ${isActive ? "text-cyan-300 bg-cyan-400/10" : "text-gray-400 hover:text-cyan-300"}`}>
              <Plug size={20} aria-hidden="true" /><span className="hidden sm:inline text-xs">Integrazioni</span>
            </NavLink>
            {st && (
              <Link to="/health-status" data-testid="header-health-pill" className="hidden sm:flex items-center gap-2 chip">
                <span className="w-2 h-2 rounded-full" style={{ background: st.color, boxShadow: `0 0 8px ${st.glow}` }} />
                <span className="text-gray-300">{st.label}</span>
              </Link>
            )}
            <span className="hidden 2xl:block text-xs text-gray-500">{user?.email}</span>
            <button data-testid="btn-logout" onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded-full text-gray-500 hover:text-red-400 transition-colors" title="Esci">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="pt-20 pb-28 xl:pb-10">
        {companyId ? (
          <div key={companyId}><Outlet /></div>
        ) : companiesLoaded && companies.length === 0 ? (
          <div className="max-w-md mx-auto px-4 pt-16 text-center" data-testid="no-company-empty">
            <div className="glass rounded-3xl p-8 space-y-4">
              <p className="text-gray-300 font-medium">Non fai ancora parte di nessuna azienda</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Crea il primo workspace pulito: DAD partirà da zero e costruirà la conoscenza
                solo dalle informazioni che fornirai.
              </p>
              <button className="btn-primary w-full" data-testid="no-company-create"
                onClick={() => { document.querySelector('[data-testid="company-switcher-btn"]')?.click(); setTimeout(() => document.querySelector('[data-testid="company-create-open"]')?.click(), 150); }}>
                Crea la prima azienda
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-4 pt-16 text-center text-gray-500 text-sm" data-testid="no-company">
            Caricamento workspace…
          </div>
        )}
      </main>

      <nav data-testid="nav-mobile" className="xl:hidden fixed bottom-0 inset-x-0 z-40 glass-strong border-t border-cyan-400/10">
        <div className="grid grid-cols-5 h-16 items-center">
          {NAV.slice(0, 2).map((n) => <MobileItem key={n.to} {...n} />)}
          <div className="flex justify-center">
            <Link to="/scan" data-testid="btn-scansiona-mobile"
              className="scan-glow -mt-8 w-16 h-16 rounded-full flex flex-col items-center justify-center gap-0.5 text-[#030712]"
              style={{ background: "linear-gradient(135deg,#00E5FF,#3B82F6)" }}>
              <ScanLine size={22} />
              <span className="text-[8px] font-bold tracking-wider">SCAN</span>
            </Link>
          </div>
          {NAV.slice(2).map((n) => <MobileItem key={n.to} {...n} />)}
        </div>
      </nav>
    </div>
  );
}

function MobileItem({ to, label, icon: Icon }) {
  return (
    <NavLink to={to} end={to === "/"} data-testid={`nav-mobile-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex flex-col items-center gap-1 py-2 text-[10px] ${isActive ? "text-cyan-300" : "text-gray-500"}`}>
      <Icon size={20} />
      {label}
    </NavLink>
  );
}
